import React, { useState, useEffect } from 'react'
import { localisationMaster } from 'Localisation/Localisation'
import './Result.scss'
import st from './ResultDinamicWidget.module.scss'
import CheckboxToggle from 'components/ContentBox/Global/CheckboxToggle/CheckboxToggle'
import Info from '../CRM/CRMField/Info/Info'

/**
 * Компонент "Result" отвечает за отображение и редактирование результата тестирования, который включает динамически формируемый HTML-контент.
 * Пользователь может изменять текст и управлять включением/выключением определенных секций в HTML-шаблоне.
 *
 * @param {Object} props - Свойства компонента.
 * @param {function} props.changeForm - Функция, которая обновляет форму с результатами тестирования, вызывается при изменении содержимого.
 * @param {Object} props.form - Данные формы, включая HTML-содержимое, передаваемое для редактирования.
 * @param {React.RefObject} props.resultRef - Ссылка на элемент, используемая для управления скроллом или фокусировкой.
 * @returns {JSX.Element} - JSX элемент компонента Result, отображающий динамически формируемый HTML-контент.
 * Примечание:
 * Компонент включает несколько переключателей, с помощью которых можно управлять отображением различных частей контента.
 * Важно отметить, что элемент с классом `placementTestCreate__widgetBody` не связан напрямую с содержимым, генерируемым функцией `generateHTML`,
 * так как в том элементе есть моковые данные для отображения.
 * generateHTML : {{full_name}}, {{correct_answers}} , {{test_date}} , {{email}} , {{online_booking}} - шаблонные переменные, это данные отобразятся
 * после прохождения теста в виджетах
 * Текст, который вводится в элемент с классом `description__text`, сохраняется в переменной `htmlText` и затем используется в функции `generateHTML`
 * для формирования итогового HTML-контента.
 */

const Result = ({ changeForm, form, resultRef, widgetId }) => {
  const isTargetWidget = +widgetId === 23 // 23 - id виджета, шаблон которого должен отличается от других

  // Состояние для включения/выключения секций HTML
  const [selectedSections, setSelectedSections] = useState({
    showFullName: true,
    showCorrectAnswers: !isTargetWidget,
    showTestDate: true,
    showEmail: !isTargetWidget,
    showFooterButton: true,
    showLevelAnswer: !isTargetWidget,
    showTakeTestAgain: !isTargetWidget,
    showSaveResult: !isTargetWidget,
    showOralTest: true,
  })

  const [htmlText, setHtmlText] = useState(
    form.html_text?.trim() ? form.html_text : '',
  )

  // Обработчик изменения чекбоксов
  const handleCheckboxChange = (sectionName, isChecked) => {
    setSelectedSections({
      ...selectedSections,
      [sectionName]: isChecked,
    })
  }

  const handleHtmlTextChange = (e) => {
    setHtmlText(e.target.innerHTML)
    changeForm(e.target.innerHTML, 'html_text') // Обновляем в форме
  }

  useEffect(() => {
    if (!form.html_body) return

    const html = form.html_body

    setSelectedSections({
      showFullName: html.includes('{{full_name}}'),
      showCorrectAnswers: html.includes('{{correct_answers}}'),
      showTestDate: html.includes('{{test_date}}'),
      showEmail: html.includes('{{email}}'),
      showFooterButton: html.includes('{{signup_link}}'),
      showLevelAnswer: html.includes('{{result_level}}'),
      showTakeTestAgain: html.includes('btn-again'),
      showSaveResult: html.includes('btn-save'),
      showOralTest: html.includes('btn-online-test'),
    })
  }, []) // only on mount

  // Генерация HTML на основе выбранных секций
  const generateHTML = () => {
    return `
      <!DOCTYPE html>
      <html lang="ru">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap" rel="stylesheet">
        <link rel="stylesheet" href="https://phoenix.interpress.kz/widget-scripts/widget.css">
        <title>Результат письменного тестирования</title>
      </head>
      <body>
        <main>
          <div class="info">
            <h2>Результат письменного теста</h2>
            <div class="info__result">
              ${
                selectedSections.showFullName
                  ? `
              <span class="info__result-fullName">{{full_name}}</span>
              `
                  : ''
              }
              <div class="info__result-items">
                ${
                  selectedSections.showCorrectAnswers
                    ? `
                <div class="info__result-item">
                  <span class="info__result-count">{{correct_answers}}</span>
                  <p class="info__result-subtitle">correct answers</p>
                </div>`
                    : ''
                }
                
                <div class="info__result-item">
                  ${
                    selectedSections.showLevelAnswer
                      ? `                
                  <p class="info__result-level">{{result_level}}</p>
                  `
                      : ''
                  }
                  ${
                    selectedSections.showTestDate
                      ? `
                  <p class="info__block-subtitle info-date">{{test_date}}</p>
                  `
                      : ''
                  }
                </div>
              </div>
            </div>
          </div>
          <div class="description">
            <div class="description__container">
              <p class="description__text">
                  ${htmlText}
              </p>
              <br>
              ${
                selectedSections.showEmail
                  ? `
                <p class="description__text">Результат теста отправлен: ("{{email}}")</p>
              `
                  : ''
              }  
        
              <div class="description__mobile">
                  ${
                    selectedSections.showOralTest
                      ? `
                  <a href="{{online_booking}}" target="_blank" id="btn-online-test"><button class="description__btn-sign-up" id="btn-online-test">Записаться на устное тестирование</button></a> `
                      : ''
                  }
                  ${
                    selectedSections.showTakeTestAgain
                      ? `
                  <button id="btn-again" class="new_attempt attempt_mobile"  href="{{new_attempt}}">Пройти заново</button> `
                      : ''
                  }
              </div>
            </div>

          </div>
        </main>
        
        <footer class="footer">
          <div class="footer__wrapper">
          ${
            selectedSections.showTakeTestAgain
              ? `
            <button id="btn-again" class="new_attempt attempt_mobile-second" href="{{new_attempt}}">Пройти заново</button>`
              : ''
          }
          ${
            selectedSections.showSaveResult
              ? `
            <button id="btn-save" class="singUp_link" href="{{signup_link}}">Сохранить результат</button>`
              : ''
          }
          </div>
        </footer>
      </body>
      </html>
    `
  }
  // для отображения результата
  const answersUnit = () => {
    return `
      <div class="table__results-inner">
        <div class="table__results-img result-true">
          <img src="https://phoenix.interpress.kz/widget-scripts/check.webp" alt="Correct answer" />
        </div>
      </div>
      <div class="table__results-inner">
        <div class="table__results-img result-false">
          <img src="https://phoenix.interpress.kz/widget-scripts/close.webp" alt="Incorrect answer" />
        </div>
      </div>
    `
  }

  // Обновляем значение поля html_body через ref при каждом изменении чекбоксов
  useEffect(() => {
    const generatedHtml = generateHTML()
    const generateAnswerUnit = answersUnit()
    changeForm(generatedHtml, 'html_body')
    changeForm(generateAnswerUnit, 'answers_unit')
  }, [selectedSections, htmlText])

  return (
    <div className='placementTestBoxResult' ref={resultRef}>
      <div className='placementTestCreate__result'>
        <p className='placementTestBox__title'>
          {localisationMaster(
            'text_21',
            'c/tests/placement-test/create-widget',
          )}
        </p>
        <div className='placementTestBox__mainBoxLine'></div>

        {/* Чекбоксы для управления секциями */}
        <div className='placementTestBox__mainBoxLine'>
          <p className='placementTestBox__subtitle'>
            Выберите секции для включения в результат:
          </p>
          <div className='placementTestBox__manipulation'>
            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Полное имя'}
                onChange={(value) =>
                  handleCheckboxChange('showFullName', value)
                }
                checked={selectedSections.showFullName}
              />
              <div className='placementTestBox__activeField'>
                <Info title='Включить/Выключить полное имя' result />
              </div>
            </div>

            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Количество правильных ответов'}
                onChange={(value) =>
                  handleCheckboxChange('showCorrectAnswers', value)
                }
                checked={selectedSections.showCorrectAnswers}
              />
              <div className='placementTestBox__activeField'>
                <Info
                  title='Включить/Выключить количество правильных ответов'
                  result
                />
              </div>
            </div>

            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Дата тестирования'}
                onChange={(value) =>
                  handleCheckboxChange('showTestDate', value)
                }
                checked={selectedSections.showTestDate}
              />
              <div className='placementTestBox__activeField'>
                <Info title='Включить/Выключить дату тестирования' result />
              </div>
            </div>

            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Email'}
                onChange={(value) => handleCheckboxChange('showEmail', value)}
                checked={selectedSections.showEmail}
              />
              <div className='placementTestBox__activeField'>
                <Info title='Включить/Выключить Email' result />
              </div>
            </div>

            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Включить уровень теста'}
                onChange={(value) =>
                  handleCheckboxChange('showLevelAnswer', value)
                }
                checked={selectedSections.showLevelAnswer}
              />
              <div className='placementTestBox__activeField'>
                <Info title='Включить/Выключить уровень теста' result />
              </div>
            </div>

            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Записаться на устное тестирование'}
                onChange={(value) =>
                  handleCheckboxChange('showOralTest', value)
                }
                checked={selectedSections.showOralTest}
              />
              <div className='placementTestBox__activeField'>
                <Info
                  title='Включить/Выключить записаться на устное тестирование'
                  result
                />
              </div>
            </div>

            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Пройти заново'}
                onChange={(value) =>
                  handleCheckboxChange('showTakeTestAgain', value)
                }
                checked={selectedSections.showTakeTestAgain}
              />
              <div className='placementTestBox__activeField'>
                <Info title='Включить/Выключить кнопку пройти заново' result />
              </div>
            </div>
            <div className='placementTestBox__toggleLine'>
              <CheckboxToggle
                right={'Сохранить результат'}
                onChange={(value) =>
                  handleCheckboxChange('showSaveResult', value)
                }
                checked={selectedSections.showSaveResult}
              />
              <div className='placementTestBox__activeField'>
                <Info
                  title='Включить/Выключить кнопку Сохранить результат'
                  result
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* отображения динамического html шаблона, generateHTML и этот блок разные */}
      <div className='placementTestCreate__widgetBody'>
        <div className='placementTestCreate__widgetBody--main'>
          <div className={st.info}>
            {!isTargetWidget && <h2>Результат письменного теста</h2>}
            <div className={st.info__result}>
              {selectedSections.showFullName && (
                <span className={st.info__result__fullName}>
                  Dinmukhamed Toleugazy
                </span>
              )}
              <div className={st.info__result__items}>
                {selectedSections.showCorrectAnswers && (
                  <div className={st.info__result__item}>
                    <span className={st.info__result_count}>16</span>
                    <p className={st.info__resultSubtitle}>correct answers</p>
                  </div>
                )}
                <div className={st.info__result__item}>
                  {selectedSections.showLevelAnswer && (
                    <p className={st.info__resultLevel}>Level 1(A1-A2)</p>
                  )}
                  {selectedSections.showTestDate && (
                    <p className={`${st.infoDate}`}>August 15, 2024</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={st.description}>
            <div className={st.description__container}>
              <div
                className={st.description__text}
                contentEditable
                suppressContentEditableWarning={true}
                dangerouslySetInnerHTML={{ __html: htmlText }}
                onBlur={handleHtmlTextChange}
              ></div>

              {selectedSections.showEmail && (
                <p className={st.description__text}>
                  Результат теста отправлен: (
                  <span>interpress_official.gmail.com</span>)
                </p>
              )}
              <div className={st.description__mobile}>
                {selectedSections.showOralTest && (
                  <a>
                    <button disabled className={st.description__btnSignUp}>
                      Записаться на устное тестирование
                    </button>
                  </a>
                )}
                {selectedSections.showTakeTestAgain && (
                  <button
                    disabled
                    className={`${st.newAttempt} ${st.attempt_mobile}`}
                  >
                    Пройти заново
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <footer className={st.footer}>
          <div className={st.footer__wrapper}>
            {selectedSections.showTakeTestAgain && (
              <button
                disabled
                className={`${st.newAttempt}  ${st.attempt_mobileSecond}`}
              >
                Пройти заново
              </button>
            )}
            {selectedSections.showSaveResult && (
              <button disabled className={st.singUpLink}>
                Сохранить результат
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  )
}

export default Result
